import { BookingEventSetupState } from '@deltager/organizers/admin/modules/booking-event-setup/store/booking-event-setup.state';
import { EventSetupState } from '@deltager/organizers/admin/modules/events/create-event/store/event-setup.state';
import { ImageSource } from '../shared/models/ImageSource';
import { Action, createFeatureSelector } from '@ngrx/store';

import { OrganizerSettings } from '@deltager/organizers/admin/shared/models/OrganizerSettings';
import { BookingReportState } from '../modules/booking-report/store/booking-report.state';
import { DntEventsListState } from '@deltager/organizers/admin/modules/events/events-list-main/dnt-events-list/store/dnt-events-list.state';
import { ReportingState } from '@deltager/organizers/admin/modules/reporting/reporting-main/store/reporting.state';

export interface RootState {
  event: EventSetupState;
  booking: BookingEventSetupState;
  activeImageSources: ImageSource[];
  organizer: OrganizerSettings;
  isEditableContentMode: boolean;
  bookingReport: BookingReportState;
  dntEventsList: DntEventsListState;
  reporting: ReportingState;
}

export const INITIAL_ROOT_STATE: RootState = {
  event: null,
  booking: null,
  activeImageSources: null,
  organizer: null,
  isEditableContentMode: false,
  bookingReport: null,
  dntEventsList: {} as DntEventsListState,
  reporting: {} as ReportingState,
};

export const activeImageSourcesState = createFeatureSelector<ImageSource[]>('activeImageSources');
export class UpdateActiveImageSourcesAction implements Action {
  readonly type = RootActionTypes.UpdateActiveImageSources;
  constructor(public activeImageSources: ImageSource[]) {}
}

export const selectOrganizer = createFeatureSelector<OrganizerSettings>('organizer');
export class SetOrganizerAction implements Action {
  readonly type = RootActionTypes.SetOrganizer;
  constructor(public organizer: OrganizerSettings) {}
}

export class SetOrganizerIdentityValidatedAction implements Action {
  readonly type = RootActionTypes.SetOrganizerIdentityValidated;
  constructor(public isIdentityVerified: boolean) {}
}

export class SetOrganizerHasPublishedEventsAction implements Action {
  readonly type = RootActionTypes.SetOrganizerHasPublishedEvents;
  constructor(public hasPublishedEvents: boolean) {}
}

export const selectEditContentMode = createFeatureSelector<boolean>('isEditableContentMode');
export class SwitchEditContentAction implements Action {
  readonly type = RootActionTypes.SwitchEditContentMode;
  constructor(public isEditableContentMode: boolean) {}
}

export enum RootActionTypes {
  UpdateActiveImageSources = '[Event setup] Update active image sources',
  SetOrganizer = '[Global] Set organizer',
  SetOrganizerIdentityValidated = '[Global] Set organizer identity validated',
  SetOrganizerHasPublishedEvents = '[Global] Set organizer has published events',
  SwitchEditContentMode = '[Global] Update edit content mode',
}

export enum SaveChangesSource {
  fotoWeb = 0,
  onboard = 1,
}

export const activeImageSourcesReducer = (state: ImageSource[] = null, action: Action): ImageSource[] => {
  switch (action.type) {
    case RootActionTypes.UpdateActiveImageSources: {
      return (action as UpdateActiveImageSourcesAction).activeImageSources;
    }
    default:
      return state;
  }
};

export const organizerReducer = (state: OrganizerSettings = null, action: Action): OrganizerSettings => {
  switch (action.type) {
    case RootActionTypes.SetOrganizer: {
      const organizer = (action as SetOrganizerAction).organizer;
      return organizer;
    }
    case RootActionTypes.SetOrganizerIdentityValidated: {
      if (!state) {
        return state;
      }
      const newState = Object.assign(Object.create(Object.getPrototypeOf(state)), state);
      newState.properties = { ...state.properties, isIdentityVerified: true };
      return newState;
    }
    case RootActionTypes.SetOrganizerHasPublishedEvents: {
      if (!state) {
        return state;
      }
      const newState = Object.assign(Object.create(Object.getPrototypeOf(state)), state);
      newState.properties = { ...state.properties, hasPublishedEvents: true };
      return newState;
    }
    default:
      return state;
  }
};

export const editContentModeReducer = (state: boolean = false, action: Action): boolean => {
  switch (action.type) {
    case RootActionTypes.SwitchEditContentMode: {
      return (action as SwitchEditContentAction).isEditableContentMode;
    }
    default:
      return state;
  }
};
